import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';

const useLegislationRecentUpdates = ({ legislationId } = {}) => {
  const { isAuthenticated } = useAuth0();
  const { generalApiCall } = useGeneralApiCall();
  const [updates, setUpdates] = useState([]);
  const [updatesTotal, setUpdatesTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadLegislationRecentUpdates = async ({ source, pageNumber = 1, pageSize = 20 } = {}) => {
    if (!legislationId) {
      setUpdates([]);
      setUpdatesTotal(0);
      setIsLoading(false);
      return null;
    }

    const isInitialLoad = pageNumber === 1;
    if (isInitialLoad) {
      setIsLoading(true);
    }
    try {
      const requestProperties = {
        query: '',
        type: 'PrimaryLegislationUpdate',
        filters: [{ field: 'primaryLegislationId', value: String(legislationId), operator: 'str_eq' }],
        sort: { field: 'dateTime', order: 'desc' },
        pageNumber,
        pageSize,
      };
      const results = await generalApiCall({
        pathname: '/api/search/query',
        method: 'post',
        requestProperties,
        requestSource: source,
        needsAuthentication: isAuthenticated,
        notShowErrorMessage: true,
      });
      const hits = results?.hits ?? [];
      if (!source?.signal?.aborted) {
        setUpdates(pageNumber > 1 ? (prev) => [...prev, ...hits] : hits);
        setUpdatesTotal(results?.totalHits ?? 0);
      }
      return results;
    } catch (error) {
      return null;
    } finally {
      if (isInitialLoad && !source?.signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const source = new AbortController();
    loadLegislationRecentUpdates({ source });
    return () => {
      source.abort('Legislation updates request cancelled by the user.');
      setUpdates([]);
      setUpdatesTotal(0);
    };
  }, [legislationId]);

  return { updates, updatesTotal, isLoading, loadLegislationRecentUpdates };
};

export default useLegislationRecentUpdates;
